import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import * as moment from 'moment';
import { DataApiService } from './data-api-service';
import { ConfigService } from 'src/app/service/config.service';
import { RoutingService } from 'src/app/service/routing.service';
import { ParamsBuilder } from '../../utils/params-builder';
import { DateUtils } from '../../utils/date.utils';
import { QueryParams } from '../../models/query-params.interface';

export interface VolumeTimeSeriesPoint {
  date: string;
  volume: number;
}

export interface VolumeTimeSeriesResponse {
  volume_ts: VolumeTimeSeriesPoint[];
}

export interface VolumeTimeSeriesOptions {
  /** Full-history mode for the chart navigator: `[config startDate, tomorrow]`, max-timeframe clamp skipped. */
  izoom?: boolean;
}

/**
 * Volume time series (`volume_ts`, Data API v1). Replaces the legacy
 * `yukkApi.scoresTimeSeries('volume', ...)` path: same URL-param mapping via
 * `ParamsBuilder` (no `period`), entity/portfolio/query routing made explicit.
 */
@Injectable({
  providedIn: 'root',
})
export class VolumeTimeSeriesService {
  constructor(
    private dataApiService: DataApiService,
    private config: ConfigService,
    private routing: RoutingService,
  ) {}

  getEntityVolumeTimeSeries(
    entityType: string,
    entityId: string,
    queryParams: QueryParams,
    options: VolumeTimeSeriesOptions = {},
  ): Observable<VolumeTimeSeriesResponse> {
    return this.dataApiService
      .get<VolumeTimeSeriesResponse>(
        `${entityType}/${entityId}/volume_ts`,
        this.buildParams(queryParams, options),
      )
      .pipe(map((resp) => JSON.parse(JSON.stringify(resp))));
  }

  getPortfolioVolumeTimeSeries(
    portfolioContent: string[],
    queryParams: QueryParams,
    options: VolumeTimeSeriesOptions = {},
  ): Observable<VolumeTimeSeriesResponse> {
    if (!portfolioContent.length) {
      return of({ volume_ts: [] });
    }
    return this.dataApiService
      .post<VolumeTimeSeriesResponse>(
        'portfolio/volume_ts',
        portfolioContent,
        this.buildParams(queryParams, options),
      )
      .pipe(map((resp) => JSON.parse(JSON.stringify(resp))));
  }

  /** Query/search context — body must be a resolved query payload (resolveQuery/resolveQueryGroup), never the raw auth query. */
  getQueryVolumeTimeSeries(
    queryPayload: unknown,
    queryParams: QueryParams,
    options: VolumeTimeSeriesOptions = {},
  ): Observable<VolumeTimeSeriesResponse> {
    return this.dataApiService
      .post<VolumeTimeSeriesResponse>(
        'search_query/volume_ts',
        queryPayload,
        this.buildParams(queryParams, options),
      )
      .pipe(map((resp) => JSON.parse(JSON.stringify(resp))));
  }

  private buildParams(
    queryParams: QueryParams,
    options: VolumeTimeSeriesOptions,
  ): Record<string, unknown> {
    const params = ParamsBuilder.build(
      queryParams,
      ParamsBuilder.PARAMS_FOR_ALL_REQUESTS,
      this.config.appConfig.routing.apiproj,
      { isReader: this.routing.isReader() || this.routing.isStoryReader() },
    );
    if (options.izoom) {
      params['from'] = DateUtils.fromTo(
        this.config.appConfig.setup.startDate,
      ).dateFrom;
      params['to'] = moment().add(1, 'days').format('YYYY-MM-DD');
      params['izoom'] = true;
    }
    return params;
  }
}
